/** A compact "saved signature" panel: previews the clinician's stored
 *  e-signature (or prompts them to add one) with a link out to the full
 *  signature manager.
 *
 *  Shared by the LMN attach dialog and the referral wizard so both surfaces
 *  present the signature identically. The copy is caller-supplied because the
 *  two flows describe *where* the signature is embedded differently ("letter"
 *  vs "referral packet").
 */

import { Button } from '@/shared/ui/shadcn/button';

import type { ClinicianSignatureT } from '../model/signatureTypes';
import { SignaturePreviewImage } from './SignaturePreviewImage';

interface SavedSignatureBlockProps {
  signature: ClinicianSignatureT | null;
  isLoading: boolean;
  isError: boolean;
  onManageSignature: () => void;
  /** Shown when no signature is saved yet. Defaults to the LMN wording. */
  emptyPrompt?: string;
}

export function SavedSignatureBlock({
  signature,
  isLoading,
  isError,
  onManageSignature,
  emptyPrompt = "You haven't saved an e-signature yet. Add one and we'll embed it in the document.",
}: SavedSignatureBlockProps) {
  return (
    <div className="my-3 rounded-md border border-border bg-muted/30 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Saved signature
        </span>
        {signature !== null ? (
          <Button variant="link" className="h-auto p-0 text-xs" onClick={onManageSignature}>
            Change
          </Button>
        ) : null}
      </div>
      {isError ? (
        <div className="text-sm text-destructive">
          Couldn&apos;t load your saved signature. Open the panel to retry.
        </div>
      ) : isLoading ? (
        <div className="text-sm text-muted-foreground">Loading signature…</div>
      ) : signature === null ? (
        <div className="flex flex-col gap-3">
          <div className="text-sm text-muted-foreground">{emptyPrompt}</div>
          <div>
            <Button size="sm" onClick={onManageSignature}>
              Add signature
            </Button>
          </div>
        </div>
      ) : (
        <SignaturePreviewImage signature={signature} compact />
      )}
    </div>
  );
}
